import React from "react";
import PropTypes from "prop-types";
import {
  Popover,
  Button,
  makeStyles,
  PopoverTrigger,
  PopoverSurface,
  mergeClasses,
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody
} from "@fluentui/react-components";
import { Dismiss20Regular, Emoji24Filled } from "@fluentui/react-icons";
import { FormattedMessage, useIntl } from "react-intl";
import { useCssBreakpoints } from "react-use-css-breakpoints";

const useStyles = makeStyles({
  emojisPopoverSurface: {
    padding: "8px"
  },
  emojisPopover: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap"
  },
  emoji: {
    fontSize: "20px"
  },
  emojiButton: {
    padding: "6px",
    transition: "transform 0.2s ease-in-out",
    ":hover": {
      transform: "scale(1.15)"
    }
  },
  buttonPressed: {
    backgroundColor: "var(--primaryColorBackground)"
  },
  emojiDrawer: {
    height: "auto"
  }
});

const USE_EMOJI_POPOVER_ON_MOBILE = true;
export const EMOJI_POPOVER_VIEWPORT_PADDING = 10;
export const EMOJI_POPOVER_CONTENT_MAX_WIDTH = 360;
export const emojiPopoverContentStyle = {
  maxWidth: EMOJI_POPOVER_CONTENT_MAX_WIDTH
};
export const emojiPopoverPositioning = {
  offset: { mainAxis: 15 },
  overflowBoundary: "window",
  overflowBoundaryPadding: {
    start: EMOJI_POPOVER_VIEWPORT_PADDING,
    end: EMOJI_POPOVER_VIEWPORT_PADDING
  },
  autoSize: "width"
};

export function shouldRenderEmojiPopover(breakpoint) {
  return USE_EMOJI_POPOVER_ON_MOBILE || (breakpoint !== "sm" && breakpoint !== "md");
}

export function shouldRenderEmojiDrawer(breakpoint) {
  return !USE_EMOJI_POPOVER_ON_MOBILE && (breakpoint === "sm" || breakpoint === "md");
}

export function ToolbarEmojisButton({ styles, items, open, onOpenChange }) {
  const classes = useStyles();
  const intl = useIntl();
  const handleOpenChange = (_, data) => {
    onOpenChange(data.open || false);
  };
  const breakpoint = useCssBreakpoints();

  return (
    <>
      {shouldRenderEmojiPopover(breakpoint) && (
        <div position="center">
          <Popover
            className={styles.emojiPopover}
            open={open}
            onOpenChange={handleOpenChange}
            positioning={emojiPopoverPositioning}
          >
            <PopoverTrigger>
              <Button
                appearance="subtle"
                className={open ? mergeClasses(styles.button, classes.buttonPressed) : styles.button}
                icon={<Emoji24Filled />}
              />
            </PopoverTrigger>
            <PopoverSurface className={classes.emojisPopoverSurface}>
              <div className={classes.emojisPopover} style={emojiPopoverContentStyle}>
                {items.map(item => (
                  <Button
                    className={classes.emojiButton}
                    key={item.id}
                    appearance="transparent"
                    icon={item.icon}
                    onClick={() => {
                      item.onSelect(item);
                    }}
                  />
                ))}
              </div>
            </PopoverSurface>
          </Popover>
        </div>
      )}
      {shouldRenderEmojiDrawer(breakpoint) && (
        <>
          <OverlayDrawer
            open={open}
            onOpenChange={handleOpenChange}
            position="bottom"
            modalType="non-modal"
            className={classes.emojiDrawer}
          >
            <DrawerHeader>
              <DrawerHeaderTitle
                action={
                  <Button
                    appearance="subtle"
                    aria-label={intl.formatMessage({
                      id: "room-settings-sidebar.close-button",
                      defaultMessage: "Close"
                    })}
                    icon={<Dismiss20Regular />}
                    onClick={e => handleOpenChange(e, { open: false })}
                  />
                }
              >
                <FormattedMessage id="reaction-popover.title" defaultMessage="Emotes" />
              </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
              <div className={classes.emojisPopover}>
                {items.map(item => (
                  <Button
                    className={classes.emojiButton}
                    key={item.id}
                    appearance="transparent"
                    icon={item.icon}
                    onClick={() => {
                      item.onSelect(item);
                    }}
                  />
                ))}
              </div>
            </DrawerBody>
          </OverlayDrawer>
        </>
      )}
    </>
  );
}

ToolbarEmojisButton.propTypes = {
  styles: PropTypes.object,
  scene: PropTypes.object,
  hubChannel: PropTypes.object,
  items: PropTypes.array.isRequired,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func
};
